import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api } from '../lib/api'
import type { RiskFlag } from '../lib/types'
import { Card, EmptyState, Skeleton } from '../ui'
import styles from './p6.module.css'

const fmt = (iso: string) => new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' }).format(new Date(iso))

/** Risk Radar list: auditor resignation, rating downgrade, pledge up, CEO/CFO/MD exit, delayed results, regulator penalty. */
export function RiskRadarCard({ scope = 'mine', days = 90, limit = 8, title = 'Risk Radar', emptyHint }: {
  scope?: 'mine' | 'holdings' | 'watchlists' | 'all'; days?: number; limit?: number; title?: string; emptyHint?: string
}) {
  const [flags, setFlags] = useState<RiskFlag[] | null>(null)
  const [error, setError] = useState(false)
  useEffect(() => {
    api<{ data: RiskFlag[] }>(`/risk-radar?scope=${scope}&days=${days}&limit=${limit}`).then(r => setFlags(r.data)).catch(() => setError(true))
  }, [scope, days, limit])
  return (
    <Card title={title} variant="flush" action={<span className={styles.muted}>last {days} days</span>}>
      {error ? <EmptyState kind="error" title="Could not load Risk Radar" compact />
        : flags === null ? <div className={styles.pad}><Skeleton variant="row" lines={3} label="Loading Risk Radar" /></div>
        : flags.length === 0 ? <EmptyState title="No risk flags" compact>{emptyHint ?? 'None of your stocks has a flagged filing.'}</EmptyState>
        : (
          <ul className={styles.list} aria-label="Risk flags">
            {flags.map(f => (
              <li key={f.id} className={`${styles.item} ${styles[`sev${f.severity}`]}`}>
                <div className={styles.grow}>
                  <div className={styles.row}>
                    <Link to={`/stock/${encodeURIComponent(f.symbol)}?tab=filings`} className={styles.sym}>{f.symbol}</Link>
                    <span className={f.severity === 3 ? `${styles.pill} ${styles.pillDown}` : `${styles.pill} ${styles.pillWarn}`}>{f.label}</span>
                    {f.held && <span className={styles.pill}>Holding</span>}
                    <span className={styles.muted} style={{ marginLeft: 'auto' }}>{fmt(f.flagged_at)}</span>
                  </div>
                  <div className={styles.riskDetail}>{f.detail}</div>
                  {f.headline && <div className={styles.riskDetail}>{f.pdf_url ? <a href={f.pdf_url} target="_blank" rel="noopener noreferrer">{f.headline}</a> : f.headline}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
    </Card>
  )
}
